/*!
 * Copyright (c) 2022 WEBLIFE
 *
 * Permission is granted to use this file for the period specified in the contract 
 * between WEBLIFE and the client. Usage of this file beyond the contract period 
 * is strictly prohibited unless an extended license or written permission is obtained.
 *
 * Modifying or editing this file without prior written consent from WEBLIFE 
 * is not permitted.
 *
 * THE FILE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, 
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A 
 * PARTICULAR PURPOSE AND NONINFRINGEMENT.
 */

(() => {
  class WLSliderSwiper extends HTMLElement {
    constructor() {
      super();

      this.sliderData = null;
      this.swiper = null;
      this.countdownBar = null;
      this.initializationPromise = null;
      this.originalSlidesCount = 0;

      this.initSlider().then(() => {
        this.classList.add("is-initialized");
      });

      this.addEventListeners();
      this.addStyles();
    }

    async initSlider() {
      this.sliderData = this.extractSliderData();
      if (!this.sliderData) return;

      const mainSwiper = this.querySelector(".main-swiper");
      if (!mainSwiper) return;

      const wrapper = mainSwiper.querySelector(".swiper-wrapper");
      if (!wrapper) return;

      // 再初期化時に独自の複製が増え続けないようにする
      Array.from(wrapper.children).forEach((slide) => {
        if (slide.classList.contains("swiper-slide-duplicate")) {
          slide.remove();
        }
      });

      this.originalSlidesCount = Array.from(wrapper.children).filter(
        (slide) => slide.classList.contains("swiper-slide")
      ).length;

      if (!this.originalSlidesCount) return;

      // 表示対象が1枚だけの場合はループさせない
      this.sliderData.loop =
        this.sliderData.loop && this.originalSlidesCount > 1;

      if (this.sliderData.loop) {
        this.createLoopClones();
      }

      const options = this.setOptions(this.sliderData);
      this.swiper = new window.Swiper(mainSwiper, options);
    }

    createLoopClones() {
      const wrapper = this.querySelector(".main-swiper .swiper-wrapper");
      if (!wrapper) return;

      const slides = Array.from(wrapper.children).filter(
        (slide) =>
          slide.classList.contains("swiper-slide") &&
          !slide.classList.contains("swiper-slide-duplicate")
      );

      if (slides.length < 2) return;

      // 元画像の順序を維持しながら予備のスライドを確保する。
      // 元画像が2枚なら、元画像を含めて合計8要素にする。
      const totalSets = Math.max(2, Math.ceil(8 / slides.length));
      const fragment = document.createDocumentFragment();

      for (let set = 1; set < totalSets; set += 1) {
        slides.forEach((slide) => {
          const clone = slide.cloneNode(true);

          clone.classList.add("swiper-slide-duplicate");
          clone.removeAttribute("id");
          clone.removeAttribute("data-shopify-editor-block");
          clone.removeAttribute("data-swiper-slide-index");

          clone.querySelectorAll("[id]").forEach((element) => {
            element.removeAttribute("id");
          });

          fragment.appendChild(clone);
        });
      }

      wrapper.insertBefore(fragment, wrapper.firstChild);
    }

    extractSliderData() {
      try {
        const preview =
          window.innerWidth <= 989
            ? 1
            : parseFloat(this.dataset.sliderPreview || "1");

        return {
          speed: parseInt(this.dataset.sliderSpeed || "3000", 10),
          loop: this.dataset.sliderLoop === "true",
          arrow: this.dataset.sliderArrow === "true",
          autoloop: this.dataset.sliderAutoloop === "true",
          autorotate: this.dataset.sliderAutorotate === "true",
          autoloopSpeed: parseInt(
            this.dataset.sliderAutoloopSpeed || "1000",
            10
          ),
          preview,
          pagination: this.dataset.sliderPagination || "none",
          thumb: this.dataset.sliderThumb === "true",
          thumbSlide: this.dataset.sliderThumbSlide === "true",
          thumbGrid: parseInt(this.dataset.sliderThumbGrid || "3", 10),
          thumbPreview: parseInt(
            this.dataset.sliderThumbPreview || "3",
            10
          ),
          fade: this.dataset.sliderFade === "true",
          class: this.dataset.class || "",
          designMode: this.dataset.designMode === "true",
          parallax: this.dataset.sliderParallax === "true",
          spaceBetween: parseInt(
            this.dataset.spaceBetweenSlides || "0",
            10
          ),
          centeredSlides:
            preview % 1 === 0.5 || this.dataset.sliderLoop === "true"
        };
      } catch (error) {
        return null;
      }
    }

    setOptions(data) {
      const options = {
        speed: data.speed,
        loop: data.loop,
        loopAdditionalSlides: 0,
        slidesPerView: data.preview,
        parallax: data.parallax,

        mousewheel: {
          enabled: data.designMode,
          forceToAxis: data.designMode,
          invert: data.designMode,
          thresholdDelta: 20
        },

        on: {
          init: (swiper) => {
            swiper.el.classList.add("image-loaded");
            this.initCountdownBar(swiper);
          },

          slideChangeTransitionStart: (swiper) => {
            this.updatePagination(swiper);
            this.resetCountdownBar();
          },

          slideChange: (swiper) => {
            this.updatePagination(swiper);
            this.resetCountdownBar();
          }
        },

        breakpoints: {
          0: {
            slidesPerView: 1,
            spaceBetween: 0,
            loopAdditionalSlides: 0
          },

          750: {
            slidesPerView: data.preview,
            spaceBetween: data.spaceBetween,
            loopAdditionalSlides: data.loop ? 1 : 0
          }
        }
      };

      if (data.pagination !== "none") {
        options.pagination = {
          el: this.querySelector(".main-swiper .swiper-pagination"),
          type: data.pagination,

          renderBullet: data.loop
            ? (index, className) =>
                index < this.originalSlidesCount
                  ? `<span class="${className}"></span>`
                  : ""
            : (_, className) =>
                `<span class="${className}"></span>`,

          clickable: true,
          dynamicBullets: false
        };
      }

      if (data.autoloop) {
        options.autoplay = {
          delay: data.autoloopSpeed,
          disableOnInteraction: true,
          pauseOnMouseEnter: false,
          reverseDirection: false,
          stopOnLastSlide: false,
          waitForTransition: true
        };
      }

      if (data.arrow) {
        options.navigation = {
          nextEl: this.querySelector(".main-swiper .swiper-button-next"),
          prevEl: this.querySelector(".main-swiper .swiper-button-prev")
        };
      }

      if (data.fade) {
        options.effect = "fade";
        options.fadeEffect = {
          crossFade: true
        };
      }

      if (data.thumb && data.thumbSlide) {
        const thumbsElement = this.querySelector(".swiper-thumbs");

        if (thumbsElement) {
          const thumbsSwiper = new window.Swiper(thumbsElement, {
            slidesPerView: data.thumbPreview,
            freeMode: true,
            watchSlidesProgress: true
          });

          options.thumbs = {
            swiper: thumbsSwiper
          };
        }
      }

      if (data.spaceBetween) {
        options.spaceBetween = data.spaceBetween;
      }

      if (data.centeredSlides) {
        options.centeredSlides = true;
        options.centeredSlidesBounds = false;
      }

      if (data.loop) {
        options.slideToClickedSlide = true;
      }

      return options;
    }

    updatePagination(swiper) {
      if (!this.sliderData?.loop || !this.originalSlidesCount) return;

      // 複製数にかかわらず、元画像の枚数に対応させる
      const activeIndex = swiper.realIndex % this.originalSlidesCount;

      const bullets = this.querySelectorAll(
        ".main-swiper .swiper-pagination-bullet"
      );

      bullets.forEach((bullet, index) => {
        bullet.classList.toggle(
          "swiper-pagination-custom-active",
          index === activeIndex
        );
      });
    }

    initCountdownBar(swiper) {
      if (!this.sliderData?.autorotate) return;

      this.countdownBar = this.querySelector(".js-countdown-bar");
      this.startCountdownBar(swiper);
    }

    startCountdownBar(swiper) {
      if (!this.countdownBar) return;

      const duration =
        typeof swiper.params.autoplay === "object"
          ? swiper.params.autoplay.delay
          : 5000;

      this.countdownBar.style.transition = `width ${duration}ms linear`;
      this.countdownBar.style.width = "100%";
    }

    resetCountdownBar() {
      if (!this.countdownBar) return;

      this.countdownBar.style.transition = "none";
      this.countdownBar.style.width = "0%";

      setTimeout(() => {
        if (this.swiper && !this.swiper.destroyed) {
          this.startCountdownBar(this.swiper);
        }
      }, 50);
    }

    addEventListeners() {
      document.addEventListener("shopify:section:load", (event) => {
        const section = this.closest("section");

        if (
          !section ||
          !event.target ||
          !section.contains(event.target)
        ) {
          return;
        }

        if (this.swiper) {
          const thumbs = this.swiper.thumbs?.swiper;

          this.swiper.destroy(true, true);

          if (thumbs && !thumbs.destroyed) {
            thumbs.destroy(true, true);
          }

          this.swiper = null;
          this.dataset.animation = "false";
        }

        if (this.countdownBar) {
          this.countdownBar.style.width = "0%";
          this.countdownBar = null;
        }

        this.initializationPromise = Promise.resolve().then(async () => {
          this.sliderData = null;
          await this.initSlider();
          this.addStyles();
        });
      });

      document.addEventListener("shopify:block:select", async (event) => {
        if (this.initializationPromise) {
          await this.initializationPromise;
        }

        if (!this.swiper) return;

        const targetId = `slide-item--${event.detail.blockId}`;

        const slide = Array.from(this.swiper.slides).find(
          (element) => element.id === targetId
        );

        if (!slide) return;

        const realIndex = Number(slide.dataset.swiperSlideIndex);

        if (this.sliderData.loop && Number.isFinite(realIndex)) {
          this.swiper.slideToLoop(realIndex, 0, false);
        } else {
          const index = Array.from(this.swiper.slides).indexOf(slide);
          this.swiper.slideTo(index, 0, false);
        }

        if (this.swiper.autoplay) {
          this.swiper.autoplay.stop();
        }
      });
    }

    addStyles() {
      const container = this.querySelector(".swiper");

      const mediaElements = this.querySelectorAll(
        ".swiper-slide__media"
      );

      const textBelowImage = this.querySelector(
        ".swiper-slide__mobile-grid"
      );

      if (window.innerWidth > 749 || !textBelowImage) return;

      const containerHeight = container ? container.clientHeight : 0;
      let imageHeight = 0;

      mediaElements.forEach((element) => {
        imageHeight = Math.max(imageHeight, element.clientHeight);
      });

      const countdown = this.querySelector(
        ".swiper-pagination-countdown"
      );

      if (countdown) {
        countdown.style.bottom =
          `${containerHeight - imageHeight - 1}px`;
      }
    }
  }

  if (!customElements.get("wl-slider-swiper")) {
    customElements.define("wl-slider-swiper", WLSliderSwiper);
  }
})();
